/**
 * Global pause clock.
 *
 * One flag freezes the whole scene: vehicle motion, effects, scenario step
 * timers and screen/notification holds all consult this. Timed waits use
 * `pauseClock.wait()` so their countdown stops while paused and continues
 * exactly where it left off on resume — nothing is skipped or reset.
 */
class PauseClock {
  paused = false;

  private timers = new Set<{ remaining: number; resolve: () => void }>();
  private last = performance.now();

  constructor() {
    const tick = () => {
      const now = performance.now();
      const dt = now - this.last;
      this.last = now;
      if (!this.paused && this.timers.size) {
        for (const t of [...this.timers]) {
          t.remaining -= dt;
          if (t.remaining <= 0) {
            this.timers.delete(t);
            t.resolve();
          }
        }
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  setPaused(value: boolean): void {
    this.paused = value;
  }

  toggle(): boolean {
    this.paused = !this.paused;
    return this.paused;
  }

  /** Resolves after `ms` of *unpaused* wall-clock time. */
  wait(ms: number): Promise<void> {
    if (ms <= 0) return Promise.resolve();
    return new Promise((resolve) => this.timers.add({ remaining: ms, resolve }));
  }

  /** Resolve every pending wait immediately (used on scenario stop/exit). */
  flush(): void {
    for (const t of [...this.timers]) {
      this.timers.delete(t);
      t.resolve();
    }
  }
}

export const pauseClock = new PauseClock();
